import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { client } from '../api/client'
import { LanguageSwitcher } from '../components/LanguageSwitcher'

interface ResetPasswordForm {
  email: string
  confirmEmail: string
}

interface RateLimitError {
  rate_limited: boolean
  minutes_remaining: number
  message: string
}

export function ResetPasswordPage() {
  const { t } = useTranslation('auth')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState('')
  const [rateLimit, setRateLimit] = useState<RateLimitError | null>(null)

  const { register, handleSubmit, formState: { errors }, getValues } = useForm<ResetPasswordForm>()

  const onSubmit = async (data: ResetPasswordForm) => {
    setIsSubmitting(true)
    setError('')
    setRateLimit(null)
    
    try {
      await client.post('/auth/forgot-password/', { email: data.email })
      setSuccess(true)
    } catch (err: any) {
      console.error('Password reset error:', err.response?.data)
      if (err.response?.status === 429 && err.response?.data?.rate_limited) {
        // Handle rate limit error with minutes remaining
        setRateLimit({
          rate_limited: true,
          minutes_remaining: err.response.data.minutes_remaining,
          message: err.response.data.message
        })
      } else {
        setError(err.response?.data?.message || t('resetPassword.error'))
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  // If password reset was requested successfully
  if (success) {
    return (
      <>
        {/* Fixed Language Switcher */}
        <div className="fixed top-4 right-4 z-50">
          <LanguageSwitcher />
        </div>

        <div className="auth-container" style={{ minHeight: '100vh' }}>
          <div className="max-w-md w-full">
            {/* Standalone Logo */}
            <div className="flex justify-center mb-8">
              <img src="/images/logo.png" alt="Logo" />
            </div>

            <div className="card-neobrutalism">
              <h2 className="text-xl font-bold text-center mb-4">{t('resetPassword.successTitle')}</h2>
              <p className="text-center mb-6">
                {t('resetPassword.successMessage')}
              </p>
              <div className="flex justify-center">
                <Link to="/login" className="btn-neobrutalism">
                  {t('resetPassword.backToLogin')}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </>
    )
  }

  return (
    <>
      {/* Fixed Language Switcher */}
      <div className="fixed top-4 right-4 z-50">
        <LanguageSwitcher />
      </div>

      <div className="auth-container" style={{ minHeight: '100vh' }}>
        <div className="max-w-md w-full">
          {/* Standalone Logo */}
          <div className="flex justify-center mb-8">
            <img src="/images/logo.png" alt="Logo" />
          </div>

          <div className="card-neobrutalism mb-8">
            <h2 className="text-xl font-bold text-center mb-6">{t('resetPassword.title')}</h2>
            
            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              {error && (
                <div className="bg-red-100 border-2 border-black p-4">
                  <div className="text-sm font-bold text-red-900">{error}</div>
                </div>
              )}
              
              {rateLimit && (
                <div className="bg-orange-100 border-2 border-black p-4">
                  <div className="text-sm font-bold text-orange-900">
                    {t('resetPassword.rateLimitedWithTime', { minutes: rateLimit.minutes_remaining })}
                  </div>
                </div>
              )}
              
              <div className="space-y-4">
                <div>
                  <label htmlFor="email-input" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('resetPassword.emailLabel')}
                  </label>
                  <input
                    id="email-input"
                    {...register('email', { 
                      required: t('resetPassword.emailRequired'),
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: t('resetPassword.emailInvalid')
                      }
                    })}
                    type="email"
                    className="input-neobrutalism w-full"
                    placeholder={t('resetPassword.emailPlaceholder')}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.email.message}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="confirm-email-input" className="block text-sm font-bold text-neobrutalism-text mb-2">
                    {t('resetPassword.confirmEmailLabel')}
                  </label>
                  <input
                    id="confirm-email-input"
                    {...register('confirmEmail', { 
                      required: t('resetPassword.confirmEmailRequired'),
                      validate: value => value === getValues('email') || t('resetPassword.emailMismatch')
                    })}
                    type="email"
                    className="input-neobrutalism w-full"
                    placeholder={t('resetPassword.confirmEmailPlaceholder')}
                  />
                  {errors.confirmEmail && (
                    <p className="mt-1 text-sm font-bold text-red-600">{errors.confirmEmail.message}</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-3">
                <button 
                  type="submit" 
                  className="btn-neobrutalism w-full text-xs sm:text-base py-2 sm:py-3 px-2 sm:px-4"
                  disabled={isSubmitting || !!rateLimit}
                >
                  {isSubmitting ? t('resetPassword.submitting') : t('resetPassword.submitButton')}
                </button>
                
                <div className="text-center">
                  <Link to="/login" className="text-sm link-neobrutalism">
                    {t('resetPassword.cancel')}
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  )
} 